import { Periode } from "./Periods";
import { getPeriodeByName, PeriodeDate } from "./PeriodeDate";

export interface Week {
  periodeDate: PeriodeDate;
  name: string;
  startDate: Date;
  startDateString: string;
  endDate: Date;
  endDateString: string;
}

/**
 * Retourne les semaines sélectionnables à partir de la semaine suivante
 */
export function getSelectionableWeeks(
  periodes: string[],
  currentDate: Date = new Date()
): Week[] {

  const today = new Date(currentDate);
  today.setHours(0, 0, 0, 0);

  return getWeeksOfPeriods(periodes, currentDate)
    .filter(week => {
      const weekEnd = new Date(week.end);
      weekEnd.setHours(0, 0, 0, 0);

      return weekEnd >= today; // garde semaines futures uniquement
    })
    .map(week => ({
      periodeDate: week.periodeDate,
      name: week.name,
      startDate: week.start,
      endDate: week.end,
      startDateString: formatDate(week.start),
      endDateString: formatDate(week.end)
    }));
}


export function getWeeksOfPeriods(
  periodes: string[],
  currentDate: Date = new Date(),
  monthsAhead: number = 12
): { periodeDate: PeriodeDate; name: string; start: Date; end: Date }[] {

  const selectedWeeks: { periodeDate: PeriodeDate; name: string; start: Date; end: Date }[] = [];
  const seenMondays = new Set<string>();

  const moisEnFrancais = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  const today = new Date(currentDate);
  today.setHours(0, 0, 0, 0);

  const maxDate = new Date(today);
  maxDate.setMonth(maxDate.getMonth() + monthsAhead);

  const currentYear = today.getFullYear();

  periodes.forEach((periodeName) => {
    const currentPeriod = getPeriodeByName(periodeName);

    // On génère uniquement sur 2 années MAX pour gérer Noël
    [currentYear, currentYear + 1].forEach(year => {

      let firstMonday = getFirstMonday(
        new Date(year, currentPeriod.startMonth - 1, currentPeriod.startDay)
      );

      let weekNumber = 1;

      while (true) {

        if (!isWeekInPeriod(firstMonday, currentPeriod)) break;
        if (firstMonday > maxDate) break; // 🔥 stop futur

        const key = firstMonday.toISOString().slice(0, 10);

        if (!seenMondays.has(key)) {
          seenMondays.add(key);

          const endWeek = new Date(firstMonday);
          endWeek.setDate(endWeek.getDate() + 4);

          selectedWeeks.push({
            periodeDate: currentPeriod,
            name: `${moisEnFrancais[firstMonday.getMonth()]} ${weekNumber} ${firstMonday.getFullYear()}`,
            start: new Date(firstMonday),
            end: endWeek
          });
        }

        const oldMonth = firstMonday.getMonth();
        firstMonday.setDate(firstMonday.getDate() + 7);

        if (firstMonday.getMonth() !== oldMonth) {
          weekNumber = 1;
        } else {
          weekNumber++;
        }
      }
    });
  });

  return selectedWeeks.sort((a, b) => a.start.getTime() - b.start.getTime());
}


/**
 * Vérifie si la semaine (lundi) est dans la période
 */
function isWeekInPeriod(monday: Date, period: PeriodeDate): boolean {
  const year = monday.getFullYear();

  let startYear = year;
  let endYear = year;

  // Cas période qui traverse l'année (ex: Décembre → Janvier)
  if (period.endMonth < period.startMonth) {
    if (monday.getMonth() + 1 < period.startMonth) {
      startYear = year - 1;
      endYear = year;
    } else {
      startYear = year;
      endYear = year + 1;
    }
  }

  const periodStart = new Date(startYear, period.startMonth - 1, period.startDay);
  const periodEnd = new Date(endYear, period.endMonth - 1, period.endDay);

  return monday >= periodStart && monday <= periodEnd;
}


/**
 * Retourne le premier lundi à partir d'une date donnée
 */
function getFirstMonday(date: Date): Date {
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 1) return date; // lundi
  const diff = (8 - dayOfWeek) % 7;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
}


function isMonthsRemaining(currentDate: Date, monday: Date, currentPeriod: PeriodeDate): boolean {
  const month = monday.getMonth() + 1;
  const year = monday.getFullYear();

  if (currentPeriod.periode === Periode.NOEL_NA) {
    // Noël/Nouvel-An s'étend sur 2 ans (décembre → janvier)
    return (year === currentDate.getFullYear() || year === currentDate.getFullYear() + 1) && (month === 12 || month === 1);
  }

  // Autres périodes : vérifier si la semaine est dans l'année courante ou dans les limites de la période
  return (year >= currentDate.getFullYear()) && (month >= currentPeriod.startMonth && month <= currentPeriod.endMonth);
}


export function containsDate(date: string, week: Week): boolean {
  // Parser la date en composants locaux pour éviter les problèmes UTC
  const [year, month, day] = date.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);

  const start = new Date(week.startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(week.endDate);
  end.setHours(0, 0, 0, 0);

  return start <= d && d <= end;
}

export function filterWeeksByStageDates(
  weeks: Week[],
  stageDates: string[]
  ): Week[] {

  console.log(
    "AVANT FILTER",
    weeks.map(w => w.periodeDate.periode)
  );

  console.log("STAGE DATES", stageDates);

  const filtered = weeks.filter((week: Week) =>
    stageDates.some((date: string) => containsDate(date, week))
  );

  console.log(
    "APRES FILTER",
    filtered.map(w => w.periodeDate.periode)
  );

  return filtered;
}


export function extractAvailablePeriods(weeks: Week[]): Periode[] {
  return Array.from(new Set(weeks.map((w: Week) => w.periodeDate.periode)));
}

function formatDate(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${day}/${month}/${year}`;
}

export function dateAsString(date: Date): string {
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());
  return `${year}/${month}/${day}`;
}

export function getIntervalDates(week: Week): string[] {
  return [
    convertDateFormat(formatDate(week.startDate)),
    convertDateFormat(formatDate(week.endDate))
  ];
}

export function convertDateFormat(dateString: string): string {
  const [day, month, year] = dateString.split('/');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}
